import { dialog, InfiniteScroll } from "../../../nq-component";
import BaseListPage from "../../../base/BaseListPage.js";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  getObjectUseCase,
  upsertUseCase,
} from "../../../usecases/object/index.js";
import { getCurrentUserUseCase } from "../../../usecases/user/index.js";
import withRouter from "../../../withRouter.js";
import DialogCreate from "../../../components/DialogCreate/index.js";
import DialogSuccess from "../../../components/DialogSuccess/index.js";
import AddButton from "../../../components/AddButton/index.js";
import CustomConfirmation from "../../../components/CustomConfirmation/index.js";
import CustomLoader from "../../../components/CustomLoader/index.js";
import EmployeesListPagePresenter from "./EmployeesListPagePresenter.js";
import EmployeesTable from "./components/EmployeesTable.js";
import PageHeader from "../../../components/PageHeader/index.js";
import CardWithHeader from "../../../components/CardWithHeader/index.js";
import SearchFilterCard from "../../../components/SearchFilterCard/index.js";

class EmployeesListPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.state = {
      selected: [],
      objects: [],
      client: null,
      count: 0,
    };

    this.presenter = new EmployeesListPagePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase(),
      getCurrentUserUseCase(),
      getObjectUseCase(),
    );
  }

  getCollectionName() {
    return "employees";
  }

  getParamsMonth() {
    return this.props.params.month;
  }

  getParamsYear() {
    return this.props.params.year;
  }

  getMonths() {
    return this.presenter.getMonths();
  }

  getClientId() {
    return this.props.params.clientId;
  }

  onSubmitModal(collection, object) {
    this.presenter.onSubmitModal(collection, object);
  }

  createModal(collection, field, placeholder) {
    dialog.fire({
      html: (
        <DialogCreate
          placeholder={placeholder}
          field={field}
          collection={collection}
          onSubmitModal={this.onSubmitModal.bind(this)}
        />
      ),
      footer: false,
    });
  }

  successModal(message) {
    dialog.close();
    dialog.fire({
      html: <DialogSuccess message={message} />,
      footer: false,
    });
  }

  onSubmit(object) {
    this.presenter.onSubmit(object);
  }

  onDeleteModal(index) {
    dialog.fire({
      html: <CustomConfirmation />,
      footer: false,
    });
  }

  onCloseDialog() {
    dialog.close();
  }

  onClickAdd() {
    const isAdmin = this.isAdmin();
    const clientId = this.getClientId();

    if (isAdmin) {
      return this.navigateTo(`/employees/form-input/${clientId}`);
    }

    this.navigateTo("/employees/form");
  }

  onEdit(object) {
    const isAdmin = this.isAdmin();
    const clientId = this.getClientId();

    if (isAdmin) {
      return this.navigateTo(`/employees/form/${object?.id}/${clientId}`);
    }
    this.navigateTo(`/employees/form/${object?.id}/`);
  }

  onSetSelectedStatus(field, value) {
    const selected = this.getSelected();
    if (!selected?.length) return;
    const action = value ? "activate" : "deactivate";
    const message =
      selected.length === 1
        ? `Are you sure you want to ${action} this item?`
        : `Are you sure you want to ${action} ${selected.length} selected items?`;
    const title = value ? "Activate selected" : "Deactivate selected";
    this.showConfirmDialog(message, title, "Confirm", "Cancel")
      .then(() => this.presenter.updateBooleanFieldBulk(selected, field, value))
      .catch(() => {});
  }

  render() {
    const { objects, progress, count, selected, client } = this.state;

    const schema = this.getSchema(this.getCollectionName());
    const schemas = this.getSchemas();
    const user = this.getCurrentUser();

    const isAdmin = this.isAdmin();
    if (!schema) return <CustomLoader />;

    const fullAddress = [
      client?.billingRegion,
      client?.billingProvince,
      client?.billingCity,
      client?.billingBarangay,
      client?.billingStreet,
    ]
      .filter(Boolean)
      .join(", ");

    return (
      <>
        <div className="overflow-auto">
          <InfiniteScroll
            className="h-100"
            loadMore={this.loadMore.bind(this)}
            hasMore={!progress && count > objects.length}
          >
            <div className="p-3 p-lg-4">
              <div className="d-flex flex-column gap-4">
                <PageHeader
                  breadcrumb={[
                    { label: "Home", path: "/" },
                    { label: "Employees" },
                  ]}
                  selected={selected}
                  objects={objects}
                  count={count}
                  title={`Employees`}
                  backButton={isAdmin}
                  action={
                    selected.length > 0
                      ? () => (
                          <div className="d-flex gap-2 align-items-center">
                            <button
                              type="button"
                              className="btn bg-success text-white btn-md fw-bold"
                              onClick={() =>
                                this.onSetSelectedStatus("isActive", true)
                              }
                            >
                              <i className="bi bi-check-circle me-1" />
                              Activate
                            </button>
                            <button
                              type="button"
                              className="btn bg-secondary text-white btn-md fw-bold"
                              onClick={() =>
                                this.onSetSelectedStatus("isActive", false)
                              }
                            >
                              <i className="bi bi-x-circle me-1" />
                              Deactivate
                            </button>
                          </div>
                        )
                      : undefined
                  }
                />

                <CardWithHeader
                  title={client?.name}
                  description={client?.businessType}
                  show={client}
                >
                  <div>
                    <small>{fullAddress || ""}</small>
                  </div>
                </CardWithHeader>

                {/* Search and Filters Card */}
                <SearchFilterCard
                  schema={schema}
                  schemas={schemas}
                  onSearch={this.searchSubmit.bind(this)}
                  searchFields={{
                    firstName: { type: "String" },
                    middleName: { type: "String" },
                    lastName: { type: "String" },
                  }}
                  onFilter={this.onChangeFilter.bind(this)}
                />

                {/* Table Card */}
                {progress && objects.length === 0 ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px", padding: "3rem" }}
                  >
                    <CustomLoader />
                  </div>
                ) : (
                  <EmployeesTable
                    objects={objects}
                    schema={schema}
                    schemas={schemas}
                    selected={selected}
                    selectable
                    progress={progress}
                    onSort={this.onClickSort.bind(this)}
                    onSelect={this.onSelect.bind(this)}
                    onSelectAll={this.onSelectAll.bind(this)}
                    onEdit={this.onEdit.bind(this)}
                    onToggleBoolean={this.onToggleBoolean.bind(this)}
                  />
                )}
              </div>
            </div>
            <AddButton onClick={this.onClickAdd.bind(this)} show={!isAdmin} />
          </InfiniteScroll>
        </div>
      </>
    );
  }
}

export default withRouter(EmployeesListPage);
